import { CopyOutlined, DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons"
import type { TableColumnsType, TablePaginationConfig, TableProps } from "antd"
import { Button, Card, message, Modal, Select, Space, Table, Tabs, Tag, Tooltip } from "antd"
import Title from "antd/es/typography/Title"
import queryString from "query-string"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router"
import { Link } from "react-router-dom"
import Filters, { FilterComponent } from "~/components/Filters"
import { Paths } from "~/constants"
import { t } from "~/languages/i18n"
import { AttributesPost, CategoryData, PaginationMeta, PostData, TagData } from "~/types/types"
import { formatDatePost } from "~/utils/formatDate"
import { normalizeText } from "~/utils/helper"
import logoEn from "../../../../public/images/logo-en.png"
import logoVi from "../../../../public/images/logo-vi.png"
import { RootState } from "../../auth/services/redux/store"
import "../css/List.css"
import { addPost, checkSlug, deletePost, fetchPostId, fetchPosts } from "../services/post"

const List: React.FC = () => {
  const [dataItem, setDataItem] = useState<PostData[]>([])
  const filterRef = useRef<{ filterFunction: () => void } | null>(null)
  const [sortPaginationValues, setSortPaginationValues] = useState<{ [key: string]: string } | null>(null)
  const [needRefresh, setNeedRefresh] = useState<boolean>(false)
  const [filterValues, setFilterValues] = useState<{ [key: string]: string } | null>(null)
  const [defaultFilterValues, setDefaultFilterValues] = useState<{ [key: string]: any } | null>(null)
  const [postNameOptions, setPostNameOptions] = useState<{ value: string | number; label: string }[]>([])
  const [categoryOptions, setCategoryOptions] = useState<{ value: number; label: string }[]>([])
  const [tagOptions, setTagOptions] = useState<{ value: number; label: string }[]>([])
  const [filterTabPost, setFilterTabPost] = useState("all")
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [bulkAction, setBulkAction] = useState<string>("")
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [metaData, setMetaData] = useState<PaginationMeta>()
  const [totalItem, setTotalItem] = useState(0)

  const permissions = JSON.parse(localStorage.getItem("permissions") || "{}")

  const [postItemData, setPostItemData] = useState<PostData>()
  const account = useSelector((state: RootState) => state.account)
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(selectedRowKeys)
    },
  }

  const fetchPostsData = async (searchTerms?: { [key: string]: string }) => {
    setLoading(true)
    setDataItem([])
    try {
      const defaultPagination = {
        page: "1",
        pageSize: "20",
      }
      const allResponse = await fetchPosts({ ...defaultPagination, ...searchTerms })
      const response = allResponse && allResponse.data
      const meta = allResponse && allResponse.meta
      setMetaData(meta)

      if (response && response.length > 0) {
        setDataItem(response)
        if (searchTerms?.publicationState === "all") {
          setTotalItem(meta?.pagination?.total || 0)
        }
        setPostNameOptions(
          response.map((post: { id: number; attributes: AttributesPost }) => ({
            value: post.id,
            label: post.attributes.name,
          })),
        )

        const uniqueCategories = Array.from(
          new Map<number, { id: number; name: string }>(
            response
              .flatMap((post: { id: number; attributes: AttributesPost }) =>
                post.attributes.categories.data.map((category: CategoryData) => ({
                  id: category.id,
                  name: category.attributes.name,
                })),
              )
              .map(
                (category: { id: number; name: string }) =>
                  [category.id, category] as [number, { id: number; name: string }],
              ),
          ).values(),
        )

        setCategoryOptions(
          uniqueCategories.map((category: { id: number; name: string }) => ({
            value: category.id,
            label: category.name,
          })),
        )

        const uniqueTags = Array.from(
          new Map<string, { id: number; name: string }>(
            response
              .flatMap((post: { id: number; attributes: AttributesPost }) =>
                post.attributes.tags.data.map((tag: TagData) => ({
                  id: tag.id,
                  name: tag.attributes.name,
                })),
              )
              .map((tag: { id: number; name: string }) => [tag.id, tag] as [number, { id: number; name: string }]),
          ).values(),
        )

        setTagOptions(
          uniqueTags.map((tag: { id: number; name: string }) => ({
            value: tag.id,
            label: tag.name,
          })),
        )
        setLoading(false)
      } else {
        setLoading(false)
      }

      return response
    } catch (error) {
      message.error(t("Failed to fetch posts. Please try again."))
    }
  }

  const onSearch = useCallback(
    (filters: object) => {
      const values: { [key: string]: string } = {}

      Object.keys(filters).forEach(key => {
        const value = filters[key as keyof typeof filters]
        if (typeof value === "string") {
          values[key] = value
        }
      })

      if (!values) {
        message.error("Values are undefined")
        return
      }

      let newValue = { ...values }

      if (values.search) {
        newValue = { ...newValue, search: normalizeText(values.search) }
      }

      const searchParams = queryString.stringify(newValue, { encode: false })
      const newUrl = searchParams.length > 0 ? `${location.pathname}?${searchParams}` : location.pathname
      navigate(newUrl)
    },
    [navigate],
  )

  useEffect(() => {
    const searchParams = queryString.parse(location.search)
    const filterValues: { [key: string]: string } = {}
    const sortPaginationValues: { [key: string]: string } = {}

    for (const key of Object.keys(searchParams)) {
      const value = searchParams[key]
      if (value && typeof value === "string") {
        if (key === "page" || key === "limit" || value === "asc" || value === "desc") {
          sortPaginationValues[key] = value
        } else {
          filterValues[key] = value
        }
      }
    }

    setSortPaginationValues(sortPaginationValues)
    setFilterValues(filterValues)
    setDefaultFilterValues(filterValues)
  }, [location.search])

  useEffect(() => {
    if (!filterValues && !sortPaginationValues) return

    if (filterValues || sortPaginationValues) {
      const params = { ...filterValues, ...sortPaginationValues, publicationState: filterTabPost }
      fetchPostsData(params)
    }
  }, [filterValues, sortPaginationValues, needRefresh])

  const handleEdit = (id: number) => {
    navigate(`${Paths.POSTS.EDIT.PATH}/${id}`)
  }

  const getPostInformation = async (id: number) => {
    try {
      const res = await fetchPostId(id)
      setPostItemData(res)
    } catch (error) {
      message.error(t("Fetch page error!"))
    }
  }
  const isSlugUnique = async (slug: string): Promise<boolean> => {
    try {
      const response = await checkSlug(slug)
      return response.data.length === 0
    } catch {
      return false
    }
  }

  const generateUniqueSlug = async (
    baseSlug: string,
    baseName: string,
    baseExcerpt: string,
  ): Promise<{ slug: string; name: string; excerpt: string }> => {
    let newSlug = baseSlug
    let newName = baseName
    let newExcerpt = baseExcerpt
    let count = 1

    const match = newSlug.match(/-copy-(\d+)$/)
    if (match) {
      count = parseInt(match[1], 10) + 1
      newSlug = `${baseSlug.replace(/-copy-(\d+)$/, "")}-copy-${count}`
      newName = `${baseName.replace(/-copy-(\d+)$/, "")}-copy-${count}`
      newExcerpt = `${baseExcerpt.replace(/-copy-(\d+)$/, "")}-copy-${count}`
    } else {
      newSlug = `${baseSlug}-copy-1`
      newName = `${baseName}-copy-1`
      newExcerpt = `${baseExcerpt}-copy-1`
    }

    while (!(await isSlugUnique(newSlug))) {
      count++
      newSlug = `${baseSlug.replace(/-copy-\d*$/, "")}-copy-${count}`
      newName = `${baseName.replace(/-copy-\d*$/, "")}-copy-${count}`
      newExcerpt = `${baseExcerpt.replace(/-copy-\d*$/, "")}-copy-${count}`
    }

    return { slug: newSlug, name: newName, excerpt: newExcerpt }
  }

  useEffect(() => {
    if (postItemData) {
      handleDuplicate()
    }
  }, [postItemData])
  const handleDuplicate = async () => {
    if (postItemData) {
      const baseSlug = postItemData.attributes.slug
      const baseName = postItemData.attributes.name
      const baseExcerpt = postItemData.attributes.excerpt
      const {
        slug: uniqueSlug,
        name: uniqueName,
        excerpt: uniqueExcerpt,
      } = await generateUniqueSlug(baseSlug, baseName, baseExcerpt)

      const dataPayload = {
        author: account.user,
        name: uniqueName,
        slug: uniqueSlug,
        content: postItemData.attributes.content,
        excerpt: uniqueExcerpt,
        categories:
          postItemData.attributes.categories.data.map((cat: CategoryData) => ({
            id: cat.id,
            name: cat.attributes.name,
            slug: cat.attributes.slug,
          })) || [],
        tags:
          postItemData.attributes.tags.data.map((tag: TagData) => ({
            id: tag.id,
            name: tag.attributes.name,
            slug: tag.attributes.slug,
          })) || [],
        publishedAt: postItemData.attributes.publishedAt
          ? new Date(postItemData.attributes.publishedAt).toISOString()
          : null,
        image: postItemData.attributes.image.data || null,
        SEO: {
          id: 0,
          metaTitle: postItemData.attributes.SEO?.metaTitle,
          metaDescription: postItemData.attributes.SEO?.metaDescription,
        },
      }

      try {
        await addPost({ data: dataPayload })
        message.success({ content: t("Post duplicated successfully!"), duration: 2 })
        setPostItemData(undefined)
        fetchPostsData()
      } catch (error) {
        message.error(t("Failed to duplicate post. Please try again."))
      }
    }
  }

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: t("Confirm Delete"),
      content: t("Are you sure you want to delete this post?"),
      okText: t("Delete"),
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          setLoading(true)
          await deletePost(id)
          fetchPostsData()
          message.success("Post deleted successfully!")
        } catch {
          message.error("Handle Delete Error")
        }
      },
    })
  }

  const handleBulkDelete = async () => {
    try {
      setLoading(true)
      const ids = selectedRowKeys.map(id => Number(id))
      await Promise.all(ids.map(id => deletePost(id)))
      setSelectedRowKeys([])
      fetchPostsData()
      setLoading(false)
      message.success(t("Posts deleted successfully"))
    } catch {
      message.error(t("Handle Delete Error"))
    }
  }

  const handleApplyBulkAction = () => {
    if (bulkAction === "delete" && selectedRowKeys.length > 0) {
      Modal.confirm({
        title: t("Confirm Delete"),
        content: t("Are you sure you want to delete the selected posts?"),
        okText: t("Delete"),
        cancelText: t("Cancel"),
        onOk: handleBulkDelete,
      })
    } else if (selectedRowKeys.length === 0) {
      message.warning(t("Please select posts to apply the action."))
    } else {
      message.warning(t("Please select a valid bulk action."))
    }
  }

  const handleChangeTab = (key: string) => {
    setFilterTabPost(key)
    if (filterRef.current) {
      filterRef.current.filterFunction()
    }
    setSortPaginationValues(null)
    fetchPostsData({
      ...filterValues,
      ...sortPaginationValues,
      publicationState: key,
    })
  }

  const onChange: TableProps<PostData>["onChange"] = (pagination: TablePaginationConfig) => {
    setSortPaginationValues({
      page: pagination.current ? pagination.current.toString() : "1",
      pageSize: pagination.pageSize?.toString() || "20",
    })
  }

  const onRefresh = () => {
    if (filterRef.current) {
      filterRef.current.filterFunction()
    }
    setSortPaginationValues(null)
    setNeedRefresh(!needRefresh)
  }

  const columns: TableColumnsType<PostData> = [
    {
      title: t("Name"),
      dataIndex: ["attributes", "name"],
      key: "name",
      showSorterTooltip: false,
      sorter: (a, b) => a.attributes.name.localeCompare(b.attributes.name),
      width: "25%",
      render: (_, record) => (
        <Link
          to={`${Paths.POSTS.EDIT.PATH}/${record.id}`}
          className="text-[#1677ff] cursor-pointer transition duration-300 hover:text-[#0b5ed7] hover:underline text-base"
        >
          {record.attributes.name}
        </Link>
      ),
    },
    {
      title: t("Excerpt"),
      dataIndex: ["attributes", "excerpt"],
      key: "excerpt",
      width: "25%",
      render: (excerpt: string) => {
        return <span dangerouslySetInnerHTML={{ __html: excerpt }} className="text-base line-clamp-2" />
      },
      showSorterTooltip: false,
      sorter: (a, b) => (a.attributes?.excerpt || "").localeCompare(b.attributes?.excerpt || ""),
    },
    {
      title: t("Category"),
      dataIndex: ["attributes", "categories"],
      key: "Categories",
      width: "15%",
      render: (_, record) => {
        const categories = record?.attributes?.categories?.data || []
        return <span>{categories.map((category: CategoryData) => category.attributes.name).join(", ")}</span>
      },
      showSorterTooltip: false,
      sorter: (a, b) => a.attributes.categories.data.length - b.attributes.categories.data.length,
    },
    {
      title: t("Tags"),
      key: "tags",
      dataIndex: ["attributes", "tags"],
      width: "5%",
      showSorterTooltip: false,
      sorter: (a, b) => a.attributes.tags.data.length - b.attributes.tags.data.length,
      render: (_, record) => {
        const getTagColor = (name: string): string => {
          if (name === "special") return "volcano"
          return name.length > 5 ? "geekblue" : "green"
        }

        const renderTags = (tags: TagData[]) => {
          return tags.map(tag => {
            const tagName = tag?.attributes?.name ?? ""
            const color = getTagColor(tagName)

            return (
              <Tag key={tag.id} color={color} className="m-0">
                {tagName}
              </Tag>
            )
          })
        }

        const tags = record?.attributes?.tags?.data ?? []

        return (
          <div className="flex flex-wrap gap-2">
            {tags.length > 0 ? renderTags(tags) : <span className="text-gray-400">—</span>}
          </div>
        )
      },
    },
    {
      title: t("Create At"),
      dataIndex: ["attributes"],
      key: "createdAt",
      width: "10%",
      render: (data: AttributesPost) => {
        const formattedDate = formatDatePost(data.createdAt)
        return (
          <span className="font-normal">
            {data.publishedAt ? t("Published") : t("Last Modified")}
            <br />
            <span>{formattedDate}</span>
          </span>
        )
      },
    },
    {
      title: t("Status"),
      dataIndex: ["attributes", "publishedAt"],
      key: "status",
      width: "10%",
      render: (publishedAt: string) => {
        return publishedAt ? (
          <Tag color="green" className="p-1 w-full rounded text-center">
            {t("Published")}
          </Tag>
        ) : (
          <Tag color="error" className="p-1 w-full rounded text-center">
            {t("Draft")}
          </Tag>
        )
      },
    },
  ]

  if (permissions.post.update.enabled || permissions.post.delete.enabled) {
    columns.push({
      title: t("Action"),
      key: "action",
      width: "10%",
      render: (_, record) => (
        <Space size="small" className="justify-center items-center">
          {permissions.post.update.enabled && (
            <Tooltip title={t("Edit")}>
              <Button
                type="primary"
                icon={<EditOutlined />}
                className="bg-blue-500 px-4 py-2 rounded"
                onClick={() => handleEdit(record.id)}
              />
            </Tooltip>
          )}
          <Tooltip title={t("Duplicate")}>
            <Button type="default" icon={<CopyOutlined />} onClick={() => getPostInformation(record.id)} />
          </Tooltip>

          {permissions.post.delete.enabled && (
            <Tooltip title={t("Delete")}>
              <Button danger type="primary" icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
            </Tooltip>
          )}
        </Space>
      ),
    })
  }

  return (
    <div className="p-4 ">
      <div className="flex space-x-4 items-center">
        <Title level={2} className="m-0">
          {t("Post")}
        </Title>
        {permissions.post.create.enabled && (
          <Button
            onClick={() => navigate(Paths.POSTS.CREATE.PATH)}
            color="primary"
            variant="outlined"
            icon={<PlusOutlined />}
          >
            {t("Add New Post")}
          </Button>
        )}
      </div>
      <div className="flex justify-between mt-3">
        <Tabs
          defaultActiveKey="all"
          activeKey={filterTabPost}
          onChange={handleChangeTab}
          items={[
            { label: `${t("All")} (${totalItem})`, key: "all" },
            { label: `${t("Draft")}`, key: "draft" },
            { label: `${t("Published")}`, key: "published" },
          ]}
        />
      </div>
      <div className="items-center">
        <Space className=" space-x-5 flex justify-between">
          <Space className="space-x-3">
            <Select
              defaultValue={t("Bulk Actions")}
              style={{ width: 170 }}
              onChange={value => setBulkAction(value)}
              options={[
                { value: "all", label: t("Bulk Actions") },
                ...(permissions.post.delete.enabled ? [{ value: "delete", label: t("Delete") }] : []),
              ]}
            />
            <Button onClick={handleApplyBulkAction}>{t("Apply")}</Button>
          </Space>
          <Space>
            <Button onClick={onRefresh} icon={<ReloadOutlined />}>
              {t("Refresh")}
            </Button>
          </Space>
        </Space>
      </div>
      <Card type="inner" style={styles.card}>
        <Filters
          filterRef={filterRef}
          defaultFilterValues={defaultFilterValues}
          onSubmit={onSearch}
          filters={
            [
              {
                label: "Name",
                placeholder: t("Find Post Name"),
                name: "name",
                type: "text",
                options: postNameOptions,
                customStyle: "2xl:w-[30rem] w-[20rem]",
              },
              {
                label: t("Category"),
                placeholder: t("Find Category"),
                name: "categories",
                type: "select-search",
                options: categoryOptions,
              },
              {
                label: t("Tag"),
                placeholder: t("Find Tag"),
                name: "tags",
                type: "select-search",
                options: tagOptions,
              },
              {
                label: t("Language"),
                placeholder: t("Choose Language"),
                name: "locale",
                type: "select",
                options: [
                  {
                    value: "vi",
                    label: (
                      <div className="flex items-center gap-2">
                        <img src={logoVi} alt="Vietnamese" className="w-5 h-5" />
                        VI
                      </div>
                    ),
                  },
                  {
                    value: "en",
                    label: (
                      <div className="flex items-center gap-2">
                        <img src={logoEn} alt="English" className="w-5 h-5" />
                        EN
                      </div>
                    ),
                  },
                ],
                defaultValue: localStorage.getItem("language"),
              },
            ] as FilterComponent[]
          }
        />
      </Card>
      <Table
        className="mt-3 border border-[rgb(229 229 229)]"
        rowKey="id"
        rowSelection={rowSelection}
        columns={columns}
        loading={loading}
        dataSource={dataItem}
        onChange={onChange}
        pagination={{
          pageSize: 20,
          total: (metaData && metaData?.pagination?.total) || 0,
        }}
        rowClassName={(record, index) => (index % 2 === 0 ? "bg-[#f6f7f7] " : "bg-white")}
      />
    </div>
  )
}

export default List

const styles = {
  title: {
    marginTop: 0,
    marginBottom: 0,
  },
  card: {
    marginTop: "1.25rem",
    marginBottom: "1.25rem",
  },
}
